import React, { useState, useMemo } from 'react';

/* ============ GREEK LEGAL CALENDAR ============ */
const getGreekHolidays = (year) => {
  // Orthodox Easter calculation (Meeus algorithm)
  const a = year % 4, b = year % 7, c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31); // 3=March, 4=April
  const day = ((d + e + 114) % 31) + 1;
  const julianEaster = new Date(year, month - 1, day);
  // Convert Julian to Gregorian (+13 days in 21st century)
  const orthodoxEaster = new Date(julianEaster);
  orthodoxEaster.setDate(orthodoxEaster.getDate() + 13);

  const easterDate = (offset) => {
    const d = new Date(orthodoxEaster);
    d.setDate(d.getDate() + offset);
    return d;
  };

  return [
    { date: new Date(year, 0, 1), name: 'Πρωτοχρονιά' },
    { date: new Date(year, 0, 6), name: 'Θεοφάνεια' },
    { date: easterDate(-48), name: 'Καθαρά Δευτέρα' },
    { date: new Date(year, 2, 25), name: '25η Μαρτίου' },
    { date: easterDate(-2), name: 'Μεγάλη Παρασκευή' },
    { date: easterDate(0), name: 'Πάσχα' },
    { date: easterDate(1), name: 'Δευτέρα Πάσχα' },
    { date: new Date(year, 4, 1), name: 'Εργατική Πρωτομαγιά' },
    { date: easterDate(50), name: 'Αγίου Πνεύματος' },
    { date: new Date(year, 7, 15), name: 'Κοίμηση Θεοτόκου' },
    { date: new Date(year, 9, 28), name: '28η Οκτωβρίου' },
    { date: new Date(year, 11, 25), name: 'Χριστούγεννα' },
    { date: new Date(year, 11, 26), name: 'Σύναξη Θεοτόκου' },
  ];
};

const isHoliday = (date) => {
  const holidays = getGreekHolidays(date.getFullYear());
  return holidays.find(h =>
    h.date.getDate() === date.getDate() &&
    h.date.getMonth() === date.getMonth()
  );
};

const isJudicialRecess = (date) => {
  // 1-31 Αυγούστου
  return date.getMonth() === 7; // August = 7
};

const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

const addBusinessDays = (startDate, days, excludeRecess = true) => {
  const result = new Date(startDate);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (isWeekend(result)) continue;
    if (isHoliday(result)) continue;
    if (excludeRecess && isJudicialRecess(result)) continue;
    added++;
  }
  return result;
};

const addCalendarDays = (startDate, days, skipToWorkday = true) => {
  const result = new Date(startDate);
  result.setDate(result.getDate() + days);
  // If result falls on weekend/holiday, move to next business day
  if (skipToWorkday) {
    while (isWeekend(result) || isHoliday(result)) {
      result.setDate(result.getDate() + 1);
    }
  }
  return result;
};

/* ============ PROCEDURE TEMPLATES ============ */
const PROCEDURES = {
  taktiki_monomelis: {
    name: 'Τακτική Αγωγή - Μονομελές Πρωτοδικείο',
    code: 'ΚΠολΔ 237-238 (Ν. 4335/2015)',
    icon: '⚖️',
    trigger: 'Κατάθεση Αγωγής',
    deadlines: [
      { name: 'Επίδοση Αγωγής', days: 30, type: 'calendar', from: 'start', article: 'ΚΠολΔ 215', critical: true },
      { name: 'Κατάθεση Προτάσεων (Ενάγων)', days: 100, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§1', critical: true },
      { name: 'Κατάθεση Προτάσεων (Εναγόμενος)', days: 100, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§1', critical: true },
      { name: 'Προσθήκη-Αντίκρουση (Ενάγων)', days: 115, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§2', critical: false },
      { name: 'Προσθήκη-Αντίκρουση (Εναγόμενος)', days: 115, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§2', critical: false },
      { name: 'Ορισμός Δικασίμου (αυτόματα)', days: 130, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§4', critical: false },
    ]
  },
  taktiki_polymelis: {
    name: 'Τακτική Αγωγή - Πολυμελές Πρωτοδικείο',
    code: 'ΚΠολΔ 237-238 (Ν. 4335/2015)',
    icon: '🏛️',
    trigger: 'Κατάθεση Αγωγής',
    deadlines: [
      { name: 'Επίδοση Αγωγής', days: 60, type: 'calendar', from: 'start', article: 'ΚΠολΔ 215', critical: true },
      { name: 'Κατάθεση Προτάσεων', days: 100, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§1', critical: true },
      { name: 'Προσθήκη-Αντίκρουση', days: 115, type: 'calendar', from: 'epidosi', article: 'ΚΠολΔ 237§2', critical: false },
    ]
  },
  asfalistika: {
    name: 'Ασφαλιστικά Μέτρα',
    code: 'ΚΠολΔ 682-703',
    icon: '🚨',
    trigger: 'Κατάθεση Αίτησης',
    deadlines: [
      { name: 'Επίδοση Αίτησης', days: 3, type: 'business', from: 'start', article: 'ΚΠολΔ 686§1', critical: true },
      { name: 'Συζήτηση', days: 30, type: 'calendar', from: 'start', article: 'ΚΠολΔ 686', critical: true },
      { name: 'Αγωγή μετά τα Ασφαλιστικά', days: 30, type: 'calendar', from: 'apofasi', article: 'ΚΠολΔ 693§1', critical: true },
    ]
  },
  diataghi_pliromis: {
    name: 'Διαταγή Πληρωμής',
    code: 'ΚΠολΔ 623-636',
    icon: '💰',
    trigger: 'Έκδοση Διαταγής',
    deadlines: [
      { name: 'Επίδοση Διαταγής', days: 60, type: 'calendar', from: 'start', article: 'ΚΠολΔ 630Α§1', critical: true },
      { name: 'Ανακοπή (15 εργάσιμες)', days: 15, type: 'business', from: 'epidosi', article: 'ΚΠολΔ 632§1', critical: true },
      { name: 'Αναστολή Εκτέλεσης', days: 15, type: 'business', from: 'epidosi', article: 'ΚΠολΔ 632§2', critical: true },
      { name: 'Εκτελεστότητα', days: 20, type: 'business', from: 'epidosi', article: 'ΚΠολΔ 630Α', critical: false },
    ]
  },
  efesi: {
    name: 'Έφεση',
    code: 'ΚΠολΔ 495-521',
    icon: '📤',
    trigger: 'Επίδοση Πρωτόδικης Απόφασης',
    deadlines: [
      { name: 'Προθεσμία Έφεσης (μετά επίδοση)', days: 30, type: 'calendar', from: 'start', article: 'ΚΠολΔ 518§1', critical: true },
      { name: 'Προθεσμία Έφεσης (χωρίς επίδοση)', days: 730, type: 'calendar', from: 'start', article: 'ΚΠολΔ 518§2 (2 έτη)', critical: true },
      { name: 'Κατάθεση Προτάσεων Εφεσείοντα', days: 60, type: 'calendar', from: 'katathesi_efesis', article: 'ΚΠολΔ 524§1', critical: true },
      { name: 'Κατάθεση Προτάσεων Εφεσιβλήτου', days: 90, type: 'calendar', from: 'katathesi_efesis', article: 'ΚΠολΔ 524§1', critical: true },
    ]
  },
  anairesi: {
    name: 'Αναίρεση',
    code: 'ΚΠολΔ 552-582',
    icon: '🔄',
    trigger: 'Επίδοση Τελεσίδικης Απόφασης',
    deadlines: [
      { name: 'Προθεσμία Αναίρεσης (μετά επίδοση)', days: 30, type: 'calendar', from: 'start', article: 'ΚΠολΔ 564§1', critical: true },
      { name: 'Προθεσμία Αναίρεσης (χωρίς επίδοση)', days: 1095, type: 'calendar', from: 'start', article: 'ΚΠολΔ 564§3 (3 έτη)', critical: true },
    ]
  },
  eksosi: {
    name: 'Έξωση Μισθωτή',
    code: 'ΚΠολΔ 662-663',
    icon: '🏠',
    trigger: 'Εξώδικη Καταγγελία',
    deadlines: [
      { name: 'Επίδοση Εξωδίκου', days: 0, type: 'calendar', from: 'start', article: 'ΑΚ 608-609', critical: true },
      { name: 'Λήξη Μισθωτικής Σχέσης', days: 30, type: 'calendar', from: 'start', article: 'ΑΚ 609', critical: true },
      { name: 'Κατάθεση Αγωγής Απόδοσης', days: 60, type: 'calendar', from: 'start', article: 'ΚΠολΔ 662', critical: true },
      { name: 'Διαταγή Απόδοσης Μισθίου', days: 20, type: 'business', from: 'katathesi', article: 'ΚΠολΔ 662Α', critical: false },
    ]
  },
  poiniki: {
    name: 'Ποινική Δίκη (Πλημμέλημα)',
    code: 'ΚΠΔ',
    icon: '⚔️',
    trigger: 'Επίδοση Κλητηρίου Θεσπίσματος',
    deadlines: [
      { name: 'Εμφάνιση στο Δικαστήριο', days: 20, type: 'calendar', from: 'start', article: 'ΚΠΔ 166§1', critical: true },
      { name: 'Προθεσμία Έφεσης', days: 10, type: 'calendar', from: 'apofasi', article: 'ΚΠΔ 473§1', critical: true },
      { name: 'Αναίρεση', days: 20, type: 'calendar', from: 'apofasi', article: 'ΚΠΔ 473§2', critical: true },
    ]
  },
  dioikitiki: {
    name: 'Διοικητική Προσφυγή',
    code: 'ΚΔΔ',
    icon: '🏢',
    trigger: 'Κοινοποίηση Διοικητικής Πράξης',
    deadlines: [
      { name: 'Ενδικοφανής Προσφυγή', days: 30, type: 'calendar', from: 'start', article: 'Ν. 3900/2010', critical: true },
      { name: 'Αίτηση Ακύρωσης (ΣτΕ)', days: 60, type: 'calendar', from: 'start', article: 'ΠΔ 18/1989 αρ.46', critical: true },
      { name: 'Προσφυγή (Διοικ. Πρωτοδικείο)', days: 60, type: 'calendar', from: 'start', article: 'ΚΔΔ 66', critical: true },
      { name: 'Ανακοπή ΕΦΚΑ/ΑΑΔΕ', days: 30, type: 'calendar', from: 'start', article: 'Ειδική Νομοθεσία', critical: true },
    ]
  },
  forologiki: {
    name: 'Φορολογική Προσφυγή',
    code: 'ΚΦΔ (Ν. 4987/2022)',
    icon: '🧾',
    trigger: 'Κοινοποίηση Πράξης Φορολογίας',
    deadlines: [
      { name: 'Ενδικοφανής Προσφυγή (ΔΕΔ)', days: 30, type: 'calendar', from: 'start', article: 'ΚΦΔ 63', critical: true },
      { name: 'Απόφαση ΔΕΔ (120 ημέρες)', days: 120, type: 'calendar', from: 'endikofa', article: 'ΚΦΔ 63§5', critical: false },
      { name: 'Σιωπηρή Απόρριψη ΔΕΔ', days: 120, type: 'calendar', from: 'endikofa', article: 'ΚΦΔ 63§5', critical: true },
      { name: 'Προσφυγή Διοικ. Δικαστήριο', days: 30, type: 'calendar', from: 'apofasi_ded', article: 'ΚΦΔ 63§7', critical: true },
    ]
  },
};

const PARAGRAFES = [
  { type: 'Γενική Παραγραφή (ΑΚ 249)', years: 20, desc: 'Αξιώσεις από δικαστική απόφαση' },
  { type: 'Πενταετής (ΑΚ 250)', years: 5, desc: 'Αμοιβές, μισθώματα, τόκοι, ασφάλιστρα' },
  { type: 'Αδικοπραξία (ΑΚ 937)', years: 5, desc: 'Από γνώση ζημίας + υπόχρεου (max 20 έτη)' },
  { type: 'Εργατική (ΑΚ 250§6)', years: 5, desc: 'Αξιώσεις μισθωτών' },
  { type: 'Αξιόγραφα - Επιταγή', years: 1, desc: 'Από λήξη εξάμηνου εμφάνισης' },
  { type: 'Αξιόγραφα - Συναλλαγματική', years: 3, desc: 'Από λήξη' },
  { type: 'Φορολογική (ΚΦΔ 36)', years: 5, desc: 'Έκδοση πράξης (10 έτη σε φοροδιαφυγή)' },
  { type: 'Ασφαλιστικές Εισφορές', years: 10, desc: 'ΕΦΚΑ' },
  { type: 'Ποινική - Πλημμέλημα', years: 5, desc: 'Από τέλεση πράξης' },
  { type: 'Ποινική - Κακούργημα', years: 15, desc: 'Από τέλεση πράξης' },
];

/* ============ MAIN COMPONENT ============ */
const DeadlineCalculator = ({ onClose, onAddDeadlines }) => {
  const [step, setStep] = useState('choose'); // choose, configure, results
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [triggerDate, setTriggerDate] = useState('');
  const [epidosiDate, setEpidosiDate] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const [calculatedDeadlines, setCalculatedDeadlines] = useState([]);
  const [showParagrafi, setShowParagrafi] = useState(false);
  const [paragDate, setParagDate] = useState('');
  const [selectedParag, setSelectedParag] = useState(null);

  const upcomingHolidays = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const holidays = [...getGreekHolidays(year), ...getGreekHolidays(year + 1)];
    return holidays.filter(h => h.date > now).sort((a, b) => a.date - b.date).slice(0, 8);
  }, []);

  const calculateDeadlines = () => {
    if (!selectedProcedure || !triggerDate) return;
    const proc = PROCEDURES[selectedProcedure];
    const start = new Date(triggerDate);
    const epidosi = epidosiDate ? new Date(epidosiDate) : addCalendarDays(start, proc.deadlines[0]?.days || 30);
    const results = [];

    proc.deadlines.forEach(dl => {
      let fromDate = start;
      if (dl.from === 'epidosi') fromDate = epidosi;
      else if (dl.from === 'apofasi' || dl.from === 'apofasi_ded' || dl.from === 'katathesi_efesis' || dl.from === 'katathesi' || dl.from === 'endikofa') {
        fromDate = epidosi; // simplified: user can adjust
      }

      let deadline;
      if (dl.type === 'business') {
        deadline = addBusinessDays(fromDate, dl.days);
      } else {
        deadline = addCalendarDays(fromDate, dl.days);
      }

      const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));
      const holiday = isHoliday(deadline);
      const recess = isJudicialRecess(deadline);

      results.push({
        ...dl,
        date: deadline,
        daysLeft,
        fromDate,
        holiday: holiday?.name || null,
        recess,
        expired: daysLeft < 0,
      });
    });

    setCalculatedDeadlines(results);
    setStep('results');
  };

  const calculateParagrafi = () => {
    if (!selectedParag || !paragDate) return null;
    const p = PARAGRAFES[selectedParag];
    const start = new Date(paragDate);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + p.years);
    const daysLeft = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
    return { end, daysLeft, type: p.type, years: p.years };
  };

  const paragResult = calculateParagrafi();

  const s = {
    btn: { padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' },
    card: { background: '#FFF', borderRadius: 12, border: '1px solid #E2E8F0', padding: 16, marginBottom: 12 },
    label: { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' },
    input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, outline: 'none' },
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex' }} onClick={onClose}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'relative', background: '#F8FAFC', width: '95%', maxWidth: 1000, height: '92vh',
        margin: 'auto', borderRadius: 16, display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)', overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '16px 24px', background: '#1E293B', color: '#FFF', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                🧮 AI Υπολογιστής Προθεσμιών
              </h2>
              <p style={{ fontSize: 13, color: '#94A3B8' }}>ΚΠολΔ • ΚΠΔ • ΚΔΔ • ΚΦΔ • Αργίες • Δικαστικές Διακοπές</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowParagrafi(!showParagrafi)} style={{
                ...s.btn, background: showParagrafi ? '#F59E0B' : 'rgba(255,255,255,0.1)', color: '#FFF',
              }}>⏰ Παραγραφή</button>
              <button onClick={onClose} style={{ ...s.btn, background: 'rgba(255,255,255,0.1)', color: '#FFF' }}>✕</button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* Παραγραφή Calculator */}
          {showParagrafi && (
            <div style={{ ...s.card, border: '2px solid #F59E0B', background: '#FFFBEB', marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#92400E', marginBottom: 12 }}>⏰ Υπολογισμός Παραγραφής</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={s.label}>Τύπος Παραγραφής</label>
                  <select value={selectedParag ?? ''} onChange={e => setSelectedParag(e.target.value ? parseInt(e.target.value) : null)}
                    style={{ ...s.input, cursor: 'pointer' }}>
                    <option value="">Επιλέξτε...</option>
                    {PARAGRAFES.map((p, i) => (
                      <option key={i} value={i}>{p.type} ({p.years} έτη)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Ημ/νία Γεγονότος</label>
                  <input type="date" value={paragDate} onChange={e => setParagDate(e.target.value)} style={s.input} />
                </div>
              </div>
              {paragResult && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 12, background: '#FFF', borderRadius: 8, border: '1px solid #FCD34D' }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color: paragResult.daysLeft > 365 ? '#059669' : paragResult.daysLeft > 90 ? '#F59E0B' : '#DC2626' }}>
                    {paragResult.daysLeft > 0 ? paragResult.daysLeft : 'ΕΛΗΞΕ'}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                      {paragResult.daysLeft > 0 ? 'ημέρες μέχρι παραγραφή' : 'Η αξίωση έχει παραγραφεί!'}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748B' }}>
                      Παραγράφεται: {paragResult.end.toLocaleDateString('el-GR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Choose Procedure */}
          {step === 'choose' && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1E293B', marginBottom: 16 }}>Επιλέξτε Τύπο Διαδικασίας</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {Object.entries(PROCEDURES).map(([key, proc]) => (
                  <div key={key} onClick={() => { setSelectedProcedure(key); setStep('configure'); }} style={{
                    ...s.card, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center',
                    border: selectedProcedure === key ? '2px solid #1E293B' : '1px solid #E2E8F0',
                  }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>{proc.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B', marginBottom: 4 }}>{proc.name}</div>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{proc.code}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>{proc.deadlines.length} προθεσμίες</div>
                  </div>
                ))}
              </div>

              {/* Upcoming Holidays */}
              <div style={{ ...s.card, marginTop: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 10 }}>📅 Επερχόμενες Αργίες & Δικαστικές Διακοπές</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {upcomingHolidays.map((h, i) => (
                    <span key={i} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#FEF3C7', color: '#92400E', fontWeight: 600 }}>
                      {h.date.toLocaleDateString('el-GR', { day: 'numeric', month: 'short' })} — {h.name}
                    </span>
                  ))}
                  <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#FEE2E2', color: '#991B1B', fontWeight: 600 }}>
                    1-31 Αυγ — Δικαστικές Διακοπές
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Configure */}
          {step === 'configure' && selectedProcedure && (
            <div>
              <button onClick={() => setStep('choose')} style={{ ...s.btn, background: '#F1F5F9', color: '#475569', marginBottom: 16 }}>← Πίσω</button>
              <div style={s.card}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {PROCEDURES[selectedProcedure].icon} {PROCEDURES[selectedProcedure].name}
                </div>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>{PROCEDURES[selectedProcedure].code}</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={s.label}>{PROCEDURES[selectedProcedure].trigger} *</label>
                    <input type="date" value={triggerDate} onChange={e => setTriggerDate(e.target.value)} style={s.input} />
                  </div>
                  <div>
                    <label style={s.label}>Ημ/νία Επίδοσης (αν γνωστή)</label>
                    <input type="date" value={epidosiDate} onChange={e => setEpidosiDate(e.target.value)} style={s.input} />
                  </div>
                  <div>
                    <label style={s.label}>Αρ. Υπόθεσης (προαιρ.)</label>
                    <input type="text" value={caseRef} onChange={e => setCaseRef(e.target.value)} style={s.input} placeholder="π.χ. 2025-001" />
                  </div>
                </div>

                <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Προθεσμίες που θα υπολογιστούν:</div>
                  {PROCEDURES[selectedProcedure].deadlines.map((dl, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                      <span style={{ color: '#1E293B' }}>{dl.critical ? '🔴' : '🟡'} {dl.name}</span>
                      <span style={{ color: '#64748B' }}>{dl.article} • {dl.days} {dl.type === 'business' ? 'εργάσιμες' : 'ημέρες'}</span>
                    </div>
                  ))}
                </div>

                <button onClick={calculateDeadlines} disabled={!triggerDate} style={{
                  ...s.btn, background: triggerDate ? '#1E293B' : '#CBD5E1', color: '#FFF', width: '100%', padding: '14px 0', fontSize: 15,
                }}>
                  🧮 Υπολογισμός Προθεσμιών
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 'results' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <button onClick={() => setStep('configure')} style={{ ...s.btn, background: '#F1F5F9', color: '#475569' }}>← Τροποποίηση</button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => {
                    const text = calculatedDeadlines.map(dl =>
                      dl.date.toLocaleDateString('el-GR') + ' | ' + dl.name + ' | ' + dl.article
                    ).join('\n');
                    navigator.clipboard?.writeText(text);
                    alert('Αντιγράφηκε στο clipboard!');
                  }} style={{ ...s.btn, background: '#EFF6FF', color: '#2563EB' }}>📋 Αντιγραφή</button>
                  <button onClick={() => {
                    const text = PROCEDURES[selectedProcedure].name + '\n' + (caseRef ? 'Υπόθεση: ' + caseRef + '\n' : '') + '\n' +
                      calculatedDeadlines.map(dl =>
                        dl.date.toLocaleDateString('el-GR') + ' — ' + dl.name + ' (' + dl.article + ')' +
                        (dl.holiday ? ' ⚠️ Μετατέθηκε λόγω ' + dl.holiday : '') +
                        (dl.recess ? ' ⚠️ Δικαστικές Διακοπές' : '')
                      ).join('\n');
                    const blob = new Blob([text], { type: 'text/plain' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = 'prothesamies_' + (caseRef || 'case') + '.txt';
                    a.click();
                  }} style={{ ...s.btn, background: '#F0FDF4', color: '#059669' }}>💾 Εξαγωγή</button>
                  <button style={{ ...s.btn, background: '#FEF3C7', color: '#92400E' }}
                    onClick={() => window.open('https://calendar.google.com', '_blank')}>📅 Google Calendar</button>
                </div>
              </div>

              <div style={s.card}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1E293B', marginBottom: 4 }}>
                  {PROCEDURES[selectedProcedure]?.icon} {PROCEDURES[selectedProcedure]?.name}
                </div>
                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>
                  {PROCEDURES[selectedProcedure]?.trigger}: {new Date(triggerDate).toLocaleDateString('el-GR')}
                  {epidosiDate && ` • Επίδοση: ${new Date(epidosiDate).toLocaleDateString('el-GR')}`}
                  {caseRef && ` • Υπόθεση: ${caseRef}`}
                </div>

                {/* Visual Timeline */}
                <div style={{ position: 'relative', paddingLeft: 28 }}>
                  {calculatedDeadlines.map((dl, i) => {
                    const urgent = dl.daysLeft <= 7 && dl.daysLeft > 0;
                    const expired = dl.daysLeft < 0;
                    const color = expired ? '#DC2626' : urgent ? '#F59E0B' : dl.critical ? '#3B82F6' : '#10B981';
                    return (
                      <div key={i} style={{ marginBottom: 16, position: 'relative' }}>
                        {/* Timeline dot + line */}
                        <div style={{
                          position: 'absolute', left: -28, top: 6, width: 14, height: 14, borderRadius: '50%',
                          background: color, border: '3px solid #FFF', boxShadow: '0 0 0 2px ' + color + '40',
                        }} />
                        {i < calculatedDeadlines.length - 1 && (
                          <div style={{ position: 'absolute', left: -22, top: 22, width: 2, height: 'calc(100% + 4px)', background: '#E2E8F0' }} />
                        )}

                        <div style={{
                          background: expired ? '#FEF2F2' : urgent ? '#FFFBEB' : '#FFF',
                          borderRadius: 10, border: '1px solid ' + (expired ? '#FECACA' : urgent ? '#FCD34D' : '#E2E8F0'),
                          padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1E293B' }}>
                              {dl.critical ? '🔴' : '🟡'} {dl.name}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                              {dl.article} • {dl.date.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                            {dl.holiday && (
                              <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>⚠️ Αρχική ημ/νία σε αργία ({dl.holiday}) — μετατέθηκε</div>
                            )}
                            {dl.recess && (
                              <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>⚠️ Πέφτει σε Δικαστικές Διακοπές (Αύγουστος)</div>
                            )}
                          </div>
                          <div style={{ textAlign: 'center', minWidth: 70 }}>
                            <div style={{ fontSize: 24, fontWeight: 800, color }}>
                              {expired ? 'ΕΛΗΞΕ' : dl.daysLeft}
                            </div>
                            {!expired && <div style={{ fontSize: 10, color: '#64748B' }}>ημέρες</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeadlineCalculator;
